import React, { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkle, Plus, Trash, Trophy, Path, FilePdf } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { buildScenarioPlannerPdf } from '@/lib/wealthPdf';
import HelpHint from '@/components/HelpHint';
import FieldHelp from '@/components/FieldHelp';
import CurrencyInput from '@/components/CurrencyInput';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';

const SCENARIO_COLORS = ['#0A1128', '#C5A059', '#2D6A4F', '#C53030', '#B7791F'];

const SUCCESS_BADGE = {
  PASS: { label: 'Comfortable', color: '#2D6A4F', bg: '#2D6A4F15' },
  TIGHT: { label: 'Likely Sufficient', color: '#B7791F', bg: '#B7791F15' },
  FAIL: { label: 'Shortfall Likely', color: '#C53030', bg: '#C5303015' },
};

const defaultScenario = (label) => ({
  label,
  current_age: 45,
  retirement_age: 65,
  life_expectancy: 90,
  current_retirement_balance: 200000,
  monthly_contribution: 1500,
  pre_retirement_return: 0.07,
  post_retirement_return: 0.05,
  monthly_expenses_in_retirement: 6000,
  monthly_social_security: 2500,
  monthly_other_income: 0
});

const ScenarioPlanner = () => {
  useTrackToolUsage('scenario_planner');
  const [scenarios, setScenarios] = useState([
    { ...defaultScenario('Retire at 62'), retirement_age: 62 },
    { ...defaultScenario('Retire at 67'), retirement_age: 67 },
  ]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const addScenario = () => {
    if (scenarios.length >= 5) return;
    setScenarios([...scenarios, defaultScenario(`Scenario ${scenarios.length + 1}`)]);
  };

  const removeScenario = (idx) => {
    if (scenarios.length <= 1) return;
    setScenarios(scenarios.filter((_, i) => i !== idx));
  };

  const updateScenario = (idx, field, value) => {
    const updated = [...scenarios];
    updated[idx] = { ...updated[idx], [field]: value };
    setScenarios(updated);
  };

  const handleCompare = async () => {
    setLoading(true);
    try {
      const response = await api.post('/scenarios/compare', { scenarios });
      setResults(response.data);
      toast.success('Scenarios compared');
    } catch (error) {
      toast.error('Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!results) return;
    try {
      let wealthHealth = null;
      try {
        const whsRes = await api.get('/wealth-health-score');
        wealthHealth = whsRes.data;
      } catch (e) {
        // WHS optional
      }
      const doc = buildScenarioPlannerPdf({ scenarios, results, wealthHealth });
      doc.save(`planivance-scenarios-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  // Build chart data: merge trajectories by age
  const buildChartData = () => {
    if (!results) return [];
    const ageMap = {};
    results.scenarios.forEach((s, idx) => {
      s.trajectory.forEach(point => {
        if (!ageMap[point.age]) ageMap[point.age] = { age: point.age };
        ageMap[point.age][`scenario_${idx}`] = point.balance;
      });
    });
    return Object.values(ageMap).sort((a, b) => a.age - b.age);
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Goal-Based Scenario Planning:</strong> Compare up to 5 retirement scenarios side-by-side. Educational projections use time-value-of-money math with assumed constant returns — actual results will vary with market conditions. Consult a financial planner for personalized scenario modeling.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Scenario Planner
          </h1>
          <HelpHint
            topic="How the Scenario Planner works"
            query="scenario planner compare retire at 62 vs 67"
            testid="scenario-help-hint"
          />
        </div>
        <p className="text-base text-[#4A5568]">
          "What if I retire at 62 vs 67?" — Compare paths side-by-side
        </p>
      </div>

      {/* Scenarios */}
      <div className="space-y-4">
        {scenarios.map((s, idx) => (
          <div key={idx} className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid={`scenario-card-${idx}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-1">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: SCENARIO_COLORS[idx] }} />
                <Input
                  value={s.label}
                  onChange={(e) => updateScenario(idx, 'label', e.target.value)}
                  data-testid={`scenario-label-${idx}`}
                  className="cabinet-grotesk text-lg font-medium border-0 border-b border-slate-200 rounded-none focus:ring-0 focus:border-[#C5A059] px-0 max-w-sm"
                />
              </div>
              {scenarios.length > 1 && (
                <button onClick={() => removeScenario(idx)} data-testid={`remove-scenario-${idx}`} className="text-[#C53030] hover:text-[#9B2C2C]">
                  <Trash size={18} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Current Age
                  <FieldHelp testid={`help-scen-${idx}-age`} title="Current Age" body="Your age today, used as the starting point for projection math." suggestion="Enter your actual age." />
                </Label>
                <Input type="number" value={s.current_age} data-testid={`scenario-${idx}-current-age`}
                  onChange={(e) => updateScenario(idx, 'current_age', Number(e.target.value))} className="border-slate-300" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Retirement Age
                  <FieldHelp testid={`help-scen-${idx}-retage`} title="Retirement Age" body="The age at which you'll stop contributing and start drawing from your nest egg." suggestion="Most U.S. workers target 65–67 (full Social Security retirement age)." />
                </Label>
                <Input type="number" value={s.retirement_age} data-testid={`scenario-${idx}-retirement-age`}
                  onChange={(e) => updateScenario(idx, 'retirement_age', Number(e.target.value))} className="border-slate-300" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Life Expectancy
                  <FieldHelp testid={`help-scen-${idx}-life`} title="Life Expectancy" body="Estimate how long you want your retirement assets to last. Most planners default to age 90–95 for safety; SSA average is ~84 for men and ~87 for women." suggestion="Suggested range: age 90–95. If you have longevity in the family, use 95." />
                </Label>
                <Input type="number" value={s.life_expectancy}
                  onChange={(e) => updateScenario(idx, 'life_expectancy', Number(e.target.value))} className="border-slate-300" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Current Balance ($)
                  <FieldHelp testid={`help-scen-${idx}-balance`} title="Current Retirement Balance" body="Total balance across all retirement accounts you'll draw from in this scenario: 401(k), IRA, Roth IRA, etc." suggestion="Pull the totals from your most recent statements. You can also copy from Net Worth." />
                </Label>
                <Input type="number" value={s.current_retirement_balance}
                  onChange={(e) => updateScenario(idx, 'current_retirement_balance', Number(e.target.value))} className="border-slate-300" data-testid={`scenario-${idx}-balance`} />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Monthly Contribution
                  <FieldHelp testid={`help-scen-${idx}-contrib`} title="Monthly Contribution" body="How much you (and your employer's match) add to retirement each month, before retirement age." suggestion="A common rule of thumb is to save 15% of gross income for retirement." />
                </Label>
                <CurrencyInput value={s.monthly_contribution}
                  onValue={(v) => updateScenario(idx, 'monthly_contribution', v || 0)} testid={`scenario-${idx}-contribution`} />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Monthly Expenses in Retirement
                  <FieldHelp testid={`help-scen-${idx}-exp`} title="Monthly Expenses in Retirement" body="Your estimated monthly spending after you stop working — housing, food, healthcare, travel, etc." suggestion="A common starting point is 70–80% of your current monthly expenses." />
                </Label>
                <CurrencyInput value={s.monthly_expenses_in_retirement}
                  onValue={(v) => updateScenario(idx, 'monthly_expenses_in_retirement', v || 0)} testid={`scenario-${idx}-expenses`} />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Monthly Social Security
                  <FieldHelp testid={`help-scen-${idx}-ss`} title="Monthly Social Security" body="Your estimated monthly Social Security benefit at the retirement age above. Get this from your SSA.gov 'my Social Security' account or use the Social Security tool to optimize." suggestion="Average U.S. benefit is around $1,900/month at full retirement age." />
                </Label>
                <CurrencyInput value={s.monthly_social_security}
                  onValue={(v) => updateScenario(idx, 'monthly_social_security', v || 0)} testid={`scenario-${idx}-ss`} />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Pre-Retirement Return (%)
                  <FieldHelp testid={`help-scen-${idx}-prer`} title="Pre-Retirement Return" body="The average annual investment growth expected before retirement. A diversified stock-heavy portfolio has historically returned 7–10% pre-inflation." suggestion="Suggested value: 7%. Be conservative — better to under-estimate growth and over-fund." />
                </Label>
                <Input type="number" step="0.01" value={Number((s.pre_retirement_return * 100).toFixed(2))}
                  onChange={(e) => updateScenario(idx, 'pre_retirement_return', Number(e.target.value) / 100)} className="border-slate-300" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  Post-Retirement Return (%)
                  <FieldHelp testid={`help-scen-${idx}-postr`} title="Post-Retirement Return" body="Average annual growth expected once you start drawing — typically lower because most retirees shift to more bonds for stability." suggestion="Suggested value: 4–5%." />
                </Label>
                <Input type="number" step="0.01" value={Number(((s.post_retirement_return || 0) * 100).toFixed(2))}
                  onChange={(e) => updateScenario(idx, 'post_retirement_return', Number(e.target.value) / 100)} className="border-slate-300" data-testid={`scenario-${idx}-post-return`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        {scenarios.length < 5 && (
          <Button onClick={addScenario} data-testid="add-scenario-button" variant="outline" className="border-[#C5A059] text-[#0A1128]">
            <Plus size={18} className="mr-2" /> Add Scenario
          </Button>
        )}
        <Button onClick={handleCompare} disabled={loading} data-testid="compare-scenarios-button" className="bg-[#0A1128] hover:bg-[#152145]">
          <Path size={18} className="mr-2" />
          {loading ? 'Computing...' : 'Compare Scenarios'}
        </Button>
        {results && (
          <Button
            onClick={handleExportPdf}
            variant="outline"
            data-testid="export-scenarios-pdf-button"
            className="border-[#C5A059] text-[#0A1128]"
          >
            <FilePdf size={18} className="mr-2" />
            Save Plan as PDF
          </Button>
        )}
      </div>

      {/* Results */}
      {results && (
        <>
          {/* Recommendation */}
          {results.recommended && (
            <div className="bg-[#0A1128] text-white rounded-md p-6 dashboard-card" data-testid="scenario-recommendation">
              <div className="flex items-center gap-3 mb-2">
                <Trophy size={24} weight="duotone" className="text-[#C5A059]" />
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A0AABF]">Best Outcome</p>
              </div>
              <h2 className="cabinet-grotesk text-2xl font-medium text-[#C5A059]">{results.recommended}</h2>
              <p className="text-sm text-[#A0AABF] mt-2">Highest balance scenario: <strong className="text-white">{results.highest_balance_scenario}</strong></p>
            </div>
          )}

          {/* Trajectory Chart */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="scenario-trajectory-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Portfolio Balance Over Time</h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={buildChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="age" stroke="#4A5568" label={{ value: 'Age', position: 'insideBottom', offset: -5 }} />
                <YAxis stroke="#4A5568" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                {results.scenarios.map((s, idx) => (
                  <Line
                    key={idx}
                    type="monotone"
                    dataKey={`scenario_${idx}`}
                    name={s.label}
                    stroke={SCENARIO_COLORS[idx]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {results.scenarios.map((s, idx) => {
              const badge = SUCCESS_BADGE[s.success_indicator];
              return (
                <div key={idx} className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid={`scenario-result-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: SCENARIO_COLORS[idx] }} />
                      <h3 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">{s.label}</h3>
                    </div>
                    <span className="px-3 py-1 rounded text-xs font-semibold uppercase tracking-[0.08em]"
                      style={{ color: badge.color, backgroundColor: badge.bg }}>
                      {badge.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Retire at</p>
                      <p className="text-2xl font-semibold text-[#0A1128] ibm-plex">Age {s.retirement_age}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Balance at Retirement</p>
                      <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">${s.balance_at_retirement.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Years in Retirement</p>
                      <p className="text-xl font-semibold text-[#0A1128] ibm-plex">{s.years_in_retirement}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Annual Withdrawal Needed</p>
                      <p className="text-xl font-semibold text-[#C53030] ibm-plex">${s.annual_withdrawal_needed.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Final Balance</p>
                      <p className="text-xl font-semibold text-[#2D6A4F] ibm-plex">${s.final_balance.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">4% Rule Benchmark</p>
                      <p className="text-xl font-semibold text-[#4A5568] ibm-plex">${s.sustainable_withdrawal_4pct.toLocaleString()}/yr</p>
                    </div>
                  </div>

                  {s.depletion_age && (
                    <div className="mt-4 pt-4 border-t border-slate-200 bg-[#C53030]/5 -mx-6 -mb-6 px-6 py-3 rounded-b-md">
                      <p className="text-sm text-[#C53030]">
                        <strong>Caution:</strong> Portfolio depletes at age {s.depletion_age}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioPlanner;
